import { SCORE_CHALLENGE_PUBLIC_ID_RE } from "./scoreChallenge";
import { CHALLENGE_ROUTE_PREFIX } from "./shareUrl";
import { isScoreChallengeQaAllowedHost, SCORE_CHALLENGE_QA_PRESETS, type ScoreChallengeQaPresetId } from "./scoreChallengeQa";

/**
 * Pure URL-path helpers for the `/c/<public_id>` share-link route. This app
 * has no client-side router (see `main.ts` - a single Phaser canvas, no
 * `history`/`URLSearchParams`-driven navigation anywhere else) - these two
 * functions are the ENTIRE routing surface PR10 adds, deliberately kept
 * this small and framework-free so both call sites (`MenuScene`'s
 * one-time redirect, and `posthogClient.ts`'s privacy-critical URL
 * sanitizer) can be unit-tested without a DOM/Phaser at all.
 */

/** Extracts the `public_id` path segment from `/c/<public_id>` (with or without a trailing slash/query/hash) - `null` for anything else, including a syntactically-plausible-but-malformed id (that's `isValidScoreChallengePublicId`'s job, checked separately so an invalid-shaped id still routes to the "CHALLENGE UNAVAILABLE" screen rather than falling through to the normal menu). */
export function parseChallengeTokenFromPath(pathname: string): string | null {
  if (!pathname.startsWith(CHALLENGE_ROUTE_PREFIX)) return null;
  const rest = pathname.slice(CHALLENGE_ROUTE_PREFIX.length);
  const token = rest.split(/[/?#]/)[0];
  return token.length > 0 ? decodeURIComponent(token) : null;
}

let consumed = false;

export type InitialRoute = { type: "token"; token: string } | { type: "qaPreset"; preset: ScoreChallengeQaPresetId };

/**
 * The one-shot gate `MenuScene.create()` calls: resolves the CURRENT page
 * load's initial route - a "Beat My Score" share link (`/c/<id>`), or a
 * directly-bookmarkable, database-free QA fixture URL (`?qaPreset=<preset>`
 * on any path, e.g.
 * `https://deploy-preview-N--snake-arenaz.netlify.app/?qaPreset=beat` -
 * host-gated by the exact same allowlist as every other QA affordance,
 * `isScoreChallengeQaAllowedHost`, so it's a no-op/inert on production;
 * never touches the network - this is the SAME synthetic-fixture path the
 * Daily QA panel's "SCORE CHALLENGE PREVIEW (QA)" buttons already use, see
 * `scoreChallengeQa.ts`) - exactly ONCE per page load, `null` forever
 * after, including every later visit to `MenuScene` via MAIN MENU/BACK.
 * Deliberately ONE function (not two independently-gated ones): both
 * checks must consume the SAME one-shot flag atomically, so a caller can
 * never end up re-checking the second kind of route after the first
 * check already consumed the gate for a page load that turned out to have
 * neither. Without this, `MenuScene` re-entering after a challenge/game
 * session would re-parse the still-unchanged `window.location`
 * pathname/search and bounce straight back into `ScoreChallengeScene`,
 * trapping the player. A fresh page load (a real reload, or a new tab)
 * always resets this module-level flag, since it's simply re-imported.
 */
export function consumeInitialRoute(pathname: string, search: string, hostname: string | null | undefined): InitialRoute | null {
  if (consumed) return null;
  consumed = true;

  const token = parseChallengeTokenFromPath(pathname);
  if (token) return { type: "token", token };

  if (!isScoreChallengeQaAllowedHost(hostname)) return null;
  const raw = new URLSearchParams(search).get("qaPreset");
  if (raw && (SCORE_CHALLENGE_QA_PRESETS as readonly string[]).includes(raw)) return { type: "qaPreset", preset: raw as ScoreChallengeQaPresetId };
  return null;
}

/** Test-only: resets the one-shot gate between tests. */
export function __resetChallengeRouteForTests(): void {
  consumed = false;
}

export function isPlausibleChallengeToken(token: string): boolean {
  return SCORE_CHALLENGE_PUBLIC_ID_RE.test(token);
}

/**
 * Replaces any `/c/<anything>` path segment appearing anywhere in `value`
 * with the fixed template `/c/:token` - the ONE function every PostHog
 * property value must pass through before capture (see
 * `posthogClient.ts`'s `sanitize_properties`). Deliberately a blanket
 * string transform, not a fixed allowlist of property names: PostHog's own
 * autocapture/pageview/pageleave/exception-tracking machinery can attach a
 * URL/path to more property keys than this codebase can enumerate and keep
 * in sync (`$current_url`, `$pathname`, `$referrer`,
 * `$initial_current_url`, `$prev_pageview_pathname`, exception stack
 * frames, ...) - matching the SHAPE of a challenge URL wherever it appears
 * is the only rule that can't silently miss a new one. Case-insensitive
 * (a UUID token itself is emitted lowercase, but this must never rely on
 * that). Non-string input is returned unchanged.
 */
export function sanitizeAnalyticsUrlValue<T>(value: T): T {
  if (typeof value !== "string") return value;
  return value.replace(/\/c\/[^/?#\s"']+/gi, `${CHALLENGE_ROUTE_PREFIX}:token`) as unknown as T;
}

/** Applies `sanitizeAnalyticsUrlValue` to every own string-valued property in a properties object - the shape PostHog's `sanitize_properties` callback and this project's own `capture()` wrapper both need. Never mutates the input. */
export function sanitizeAnalyticsProperties<T extends Record<string, unknown>>(properties: T): T {
  const result = { ...properties };
  for (const key of Object.keys(result)) {
    (result as Record<string, unknown>)[key] = sanitizeAnalyticsUrlValue(result[key]);
  }
  return result;
}
